import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PlanTagSummary, type PlanTagSummaryPlan } from "@/components/plan-tag-summary";
import { RaiseSummary } from "@/components/raise-summary";
import { SourceBadge } from "@/components/deal-contacts/contacts-section";
import { STATUS_COLOR, STATUS_LABEL } from "@/components/deal-contacts/status-meta";
import { AdminContactDetailSheet } from "@/components/admin/admin-contact-detail-sheet";
import { AdminStartDeliveryPanel } from "@/components/admin/admin-start-delivery-panel";
import type { InvestorStatus } from "@/lib/contacts.functions";
import {
  getUserDealDetailAdmin,
  listUserDealContactsAdmin,
} from "@/lib/admin-deals.functions";

export const Route = createFileRoute("/_authed/admin/users_/$userId_/deals/$dealId")({
  head: () => ({
    meta: [
      { title: "Admin deal detail: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminDealDetailPage,
});

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "";

const TERMINAL = new Set(["complete", "completed", "error", "canceled", "cancelled", "failed"]);

function fmtElapsed(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function AdminDealDetailPage() {
  const { userId, dealId } = Route.useParams();
  const fetchDetail = useServerFn(getUserDealDetailAdmin);
  const fetchContacts = useServerFn(listUserDealContactsAdmin);
  const [page, setPage] = React.useState(1);
  const [openContactId, setOpenContactId] = React.useState<string | null>(null);
  const [now, setNow] = React.useState(() => Date.now());

  const detail = useQuery({
    queryKey: ["admin-deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
    refetchInterval: (q) => {
      const runs = q.state.data?.runs ?? [];
      const latest = runs[0];
      if (!latest) return false;
      return TERMINAL.has(String(latest.status ?? "")) ? false : 10000;
    },
  });
  const contacts = useQuery({
    queryKey: ["admin-deal-contacts", dealId, page],
    queryFn: () => fetchContacts({ data: { dealId, page, pageSize: 50 } }),
  });

  const d = detail.data?.deal;
  const plan = (d?.plan_json ?? {}) as PlanTagSummaryPlan & { dealSummary?: string };
  const total = contacts.data?.total ?? 0;
  const pageSize = contacts.data?.pageSize ?? 50;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const latest = detail.data?.runs?.[0];
  const live = latest && !TERMINAL.has(String(latest.status ?? "")) ? latest : null;

  React.useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link
        to="/admin/users/$userId"
        params={{ userId }}
        className="text-sm text-brand-muted underline"
      >
        Back to this user&rsquo;s deals
      </Link>

      {detail.isPending && <p className="mt-6 text-sm text-brand-muted">Loading.</p>}
      {detail.error && (
        <p className="mt-6 text-sm text-red-600">{(detail.error as Error).message}</p>
      )}

      {d && (
        <>
          <header className="mt-3">
            <h1 className="text-xl font-semibold">
              {d.name ?? "Untitled deal"}
              {d.is_internal && (
                <span className="ml-2 rounded-full border border-brand-line px-1.5 py-0.5 align-middle text-[10.5px] font-normal uppercase tracking-wide text-brand-muted">
                  Internal
                </span>
              )}
            </h1>
            <p className="mt-1 text-sm text-brand-muted">
              {d.status ?? "unknown"} &middot; created {fmtDate(d.created_at)} &middot; owner{" "}
              {d.owner_email ?? d.owner_user_id}
            </p>
          </header>

          <section className="mt-6 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <span className="text-brand-muted">Sector:</span> {d.sector ?? "not set"}
            </div>
            <div>
              <span className="text-brand-muted">Stage:</span> {d.stage ?? "not set"}
            </div>
            <div>
              <span className="text-brand-muted">Geography:</span> {d.geography ?? "not set"}
            </div>
            <div>
              <span className="text-brand-muted">Raise type:</span> {d.raise_type ?? "not set"}
            </div>
            <div>
              <span className="text-brand-muted">Deal size:</span>{" "}
              {d.deal_size_usd === null ? "not set" : `$${d.deal_size_usd.toLocaleString()}`}
            </div>
            <div>
              <span className="text-brand-muted">Investor HQ location:</span>{" "}
              {d.investor_hq_location?.length ? d.investor_hq_location.join(", ") : "any"}
            </div>
          </section>

          <div className="mt-6 space-y-4">
            <RaiseSummary plan={plan} />
            <PlanTagSummary plan={plan} />
          </div>

          <AdminStartDeliveryPanel
            dealId={dealId}
            ownerUserId={d.owner_user_id}
            ownerEmail={d.owner_email ?? null}
            hideResult={live !== null}
            onStarted={() => void detail.refetch()}
          />

          <section className="mt-8">
            <h2 className="mb-2 text-base font-semibold">Delivery History</h2>
            {live && (
              <div
                aria-live="polite"
                className="mb-3 rounded-md border border-brand-line bg-brand-paper-2 px-3 py-2 text-sm"
              >
                <span className="font-medium">Delivery in progress</span>
                {" \u00b7 "}
                {live.status ?? "unknown"}
                {live.current_stage ? ` \u00b7 ${live.current_stage}` : ""}
                {" \u00b7 "}
                {live.final_delivered ?? 0}
                {live.delivery_target ? ` / ${live.delivery_target}` : ""} delivered
                {live.created_at
                  ? ` \u00b7 ${fmtElapsed(now - new Date(live.created_at).getTime())} elapsed`
                  : ""}
              </div>
            )}
            {detail.data && detail.data.runs.length === 0 && (
              <p className="text-sm text-brand-muted">No deliveries yet.</p>
            )}
            {detail.data && detail.data.runs.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-brand-line">
                <table className="w-full text-left text-sm">
                  <thead className="bg-brand-paper-2 text-xs uppercase text-brand-muted">
                    <tr>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Stage</th>
                      <th className="px-3 py-2 text-right">Delivered / target</th>
                      <th className="px-3 py-2">Failure</th>
                      <th className="px-3 py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.runs.map((r) => (
                      <tr key={r.id} className="border-t border-brand-line align-top">
                        <td className="px-3 py-2">{r.status ?? ""}</td>
                        <td className="px-3 py-2">{r.current_stage ?? ""}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r.final_delivered ?? 0}
                          {r.delivery_target ? ` / ${r.delivery_target}` : ""}
                        </td>
                        <td className="max-w-md px-3 py-2 text-brand-muted">
                          {r.status === "error"
                            ? [r.error_class, r.error_message].filter(Boolean).join(": ")
                            : ""}
                        </td>
                        <td className="px-3 py-2 text-brand-muted">{fmtDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {detail.data && detail.data.reports.length > 0 && (
            <section className="mt-8">
              <h2 className="mb-2 text-base font-semibold">Readiness reports</h2>
              <div className="overflow-x-auto rounded-md border border-brand-line">
                <table className="w-full text-left text-sm">
                  <thead className="bg-brand-paper-2 text-xs uppercase text-brand-muted">
                    <tr>
                      <th className="px-3 py-2">Version</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Score</th>
                      <th className="px-3 py-2">Recommendation</th>
                      <th className="px-3 py-2">Failure</th>
                      <th className="px-3 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.reports.map((r) => (
                      <tr key={r.id} className="border-t border-brand-line">
                        <td className="px-3 py-2">{r.version_number ?? ""}</td>
                        <td className="px-3 py-2">{r.report_status ?? ""}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r.investor_readiness_score ?? ""}
                        </td>
                        <td className="px-3 py-2">{r.recommendation ?? ""}</td>
                        <td className="px-3 py-2 text-brand-muted">
                          {[r.error_class, r.failed_reason].filter(Boolean).join(": ")}
                        </td>
                        <td className="px-3 py-2 text-brand-muted">{fmtDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="mt-8">
            <h2 className="mb-2 text-base font-semibold">Contacts ({total})</h2>
            {contacts.isPending && <p className="text-sm text-brand-muted">Loading.</p>}
            {contacts.data && contacts.data.rows.length === 0 && (
              <p className="text-sm text-brand-muted">No contacts delivered yet.</p>
            )}
            {contacts.data && contacts.data.rows.length > 0 && (
              <>
                <div className="overflow-x-auto rounded-md border border-brand-line">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-brand-paper-2 text-xs uppercase text-brand-muted">
                      <tr>
                        <th className="px-3 py-2">Firm</th>
                        <th className="px-3 py-2 text-right">Rating</th>
                        <th className="px-3 py-2">Contact</th>
                        <th className="px-3 py-2">Title</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Tags</th>
                        <th className="px-3 py-2">Last touched</th>
                        <th className="px-3 py-2">Notes</th>
                        <th className="px-3 py-2">Outreach</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.data.rows.map((c) => (
                        <tr
                          key={c.id}
                          className="cursor-pointer border-t border-brand-line align-top hover:bg-brand-paper-2"
                          onClick={() => setOpenContactId(c.id)}
                        >
                          <td className="px-3 py-2">
                            {c.company_name ?? ""}{" "}
                            <SourceBadge
                              source={c.source as "pipeline" | "manual" | "imported"}
                            />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {c.rating_rank ?? ""}
                          </td>
                          <td className="px-3 py-2">{c.name}</td>
                          <td className="px-3 py-2 text-brand-muted">{c.title ?? ""}</td>
                          <td className="px-3 py-2">{c.email ?? ""}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block rounded-full border px-2 py-0.5 text-[11px] ${
                                STATUS_COLOR[c.investor_status as InvestorStatus] ?? ""
                              }`}
                            >
                              {STATUS_LABEL[c.investor_status as InvestorStatus] ??
                                c.investor_status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-brand-muted">{c.tags.join(", ")}</td>
                          <td className="px-3 py-2 text-brand-muted">
                            {fmtDate(c.last_touched_at)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {c.status_notes.length || ""}
                          </td>
                          <td className="px-3 py-2 text-brand-muted">
                            {c.has_outreach ? "Drafted" : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-brand-muted">
                    Page {page} of {lastPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= lastPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </section>

          <AdminContactDetailSheet
            dealContactId={openContactId}
            open={openContactId !== null}
            onOpenChange={(o) => {
              if (!o) setOpenContactId(null);
            }}
          />
        </>
      )}
    </div>
  );
}
